import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Building2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, ApiError } from '@/lib/api';
import { setSession } from '@/lib/session';

interface AuthResponse {
  user: { id: string; name: string };
  token: string;
  workspaceId?: string | null;
}

// Where ProtectedRoute was when the session ended. Only a path on this site: `//host` and `/\host`
// are protocol-relative URLs to somebody else's.
const safeNext = (next: string | null) => (next && next.startsWith('/') && !/^\/[/\\]/.test(next) ? next : '/app');

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const body: any = { email, password };
      if (mfaCode) body.mfaCode = mfaCode;

      const data = await apiFetch<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });

      setSession({ token: data.token, userName: data.user.name, workspaceId: data.workspaceId });

      toast.success(t('checkout.login_success'));
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 412) {
        setShowMfa(true);
        toast.info('Please enter the 2FA code sent to your email');
      } else {
        toast.error(error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-hero">
      <Card className="w-full max-w-md p-8 shadow-xl border-border/60 bg-[hsl(var(--surface-overlay))]">
        <div className="text-center mb-8">
          <div className="gradient-primary p-3 rounded-2xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {showMfa ? 'Two-Factor Authentication' : t('login.title')}
          </h1>
          <p className="text-muted-foreground">
            {showMfa ? 'Enter the code sent to your email' : t('login.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!showMfa ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="mfaCode">Verification Code</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mfaCode"
                  type="text"
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="pl-9 tracking-widest text-lg"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {showMfa ? 'Verifying...' : t('checkout.loading')}
              </>
            ) : (
              showMfa ? 'Verify Code' : t('login.submit')
            )}
          </Button>

          {showMfa && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await apiFetch('/auth/login', {
                      method: 'POST',
                      body: JSON.stringify({ email, password }),
                      skipAuth: true,
                    });
                  } catch (err) {
                    if (err instanceof ApiError && err.status === 412) {
                      toast.success('New code sent to your email');
                    } else {
                      toast.error(err instanceof Error ? err.message : 'Failed to resend code');
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Resend Code
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => { setShowMfa(false); setMfaCode(''); }}
              >
                Back to Login
              </Button>
            </>
          )}

          {!showMfa && (
            <div className="mt-6 space-y-3 text-center text-sm text-muted-foreground">
              <div>
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot your password?
                </button>
              </div>
              <div>
                {t('login.no_account')}{' '}
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => navigate('/signup')}
                >
                  {t('nav.signup', 'Sign up')}
                </button>
              </div>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
